import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export interface WebhookTrigger {
  id: number;
  agent_id: string;
  name: string;
  event_type: string;
  conditions: Record<string, unknown>;
  is_active: boolean;
  created_at: string | null;
}

interface CreateTriggerBody {
  event_type: string;
  conditions: Record<string, unknown>;
  name?: string;
}

export const triggersApi = createApi({
  reducerPath: 'triggersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Triggers'],
  endpoints: (builder) => ({
    getAgentTriggers: builder.query<WebhookTrigger[], string>({
      query: (agentId) => `/studio/agents/${agentId}/webhook-triggers`,
      transformResponse: (response: { triggers: WebhookTrigger[] }) => response.triggers,
      providesTags: ['Triggers'],
    }),
    createAgentTrigger: builder.mutation<WebhookTrigger, { agentId: string } & CreateTriggerBody>({
      query: ({ agentId, ...body }) => ({
        url: `/studio/agents/${agentId}/webhook-triggers`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { trigger: WebhookTrigger }) => response.trigger,
      invalidatesTags: ['Triggers'],
    }),
    deleteAgentTrigger: builder.mutation<
      { deleted: boolean; id: number },
      { agentId: string; triggerId: number }
    >({
      query: ({ agentId, triggerId }) => ({
        url: `/studio/agents/${agentId}/webhook-triggers/${String(triggerId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Triggers'],
    }),
  }),
});

export const {
  useGetAgentTriggersQuery,
  useCreateAgentTriggerMutation,
  useDeleteAgentTriggerMutation,
} = triggersApi;
